import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, ApiError } from '../../lib/api'
import { titleCase } from '../../lib/format'
import type { ListResponse, Site, UserProfile } from '../../types'
import {
  Alert, Button, Card, CardBody, CardHeader, EmptyState, Input, Select, Skeleton,
} from '../../components/ui'
import { ShieldIcon } from '../../components/icons'

/* Who may do or see what — the one screen for both halves of that question.
 *
 * BY ROLE changes what everyone holding a role can do (PATCH /admin/roles/:key).
 * Core roles are locked: super_admin, the government roles, team_leader,
 * company and miner are defined in code, and update_role() refuses them — the
 * UI says so rather than offering a control that would fail.
 *
 * BY PERSON grants one individual something extra without inventing a role
 * for them: capabilities on top of their role (profile `extra_caps`, read by
 * role_service.user_has_capability) and named trucks / equipment / loads /
 * sites (`truck_access` and friends, read by granted_*_ids and enforced by
 * _scope_to_grants server-side). */

interface Capability { key: string; group: string; label: string }
interface Role {
  key: string; title: string; tier: string; level: number
  scope: string; caps: string[]; system: boolean; active: boolean
  description?: string
}
interface TruckOption { id?: string; plate?: string; model?: string }
interface EquipmentOption { id?: string; name?: string; equipment_type?: string }
interface LoadOption { id?: string; ticket_no?: string; mineral_type?: string; tonnage?: number }

/** Roles defined in code — role_service.CORE_ROLE_KEYS refuses edits to these. */
const LOCKED_ROLES = new Set([
  'super_admin', 'gov_director', 'gov_regional', 'gov_inspector',
  'team_leader', 'company', 'miner',
])

export default function Permissions() {
  const [tab, setTab] = useState<'role' | 'person'>('role')

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Permissions</h1>
          <p className="page-head__sub">
            Grant a whole role a capability, or give one person something extra —
            without moving them to a different role.
          </p>
        </div>
      </div>

      <div className="tab-scroll" style={{ marginBottom: 'var(--sp-5)' }}>
        <Button variant={tab === 'role' ? 'primary' : 'secondary'} size="sm" onClick={() => setTab('role')}>
          By role
        </Button>
        <Button variant={tab === 'person' ? 'primary' : 'secondary'} size="sm" onClick={() => setTab('person')}>
          By person
        </Button>
      </div>

      {tab === 'role' ? <ByRole /> : <ByPerson />}
    </>
  )
}

/* ─────────────────────────── By role ─────────────────────────── */

function ByRole() {
  const qc = useQueryClient()
  const [selected, setSelected] = useState('')
  const [draft, setDraft] = useState<string[] | null>(null)

  const rolesQuery = useQuery({
    queryKey: ['admin', 'roles'],
    queryFn: () => api.get<{ ok: boolean; items: Role[]; counts: Record<string, number> }>('/admin/roles'),
  })
  const metaQuery = useQuery({
    queryKey: ['admin', 'roles', 'meta'],
    queryFn: () => api.get<{ ok: boolean; capabilities: Capability[] }>('/admin/roles/meta'),
  })

  const save = useMutation({
    mutationFn: ({ key, caps }: { key: string; caps: string[] }) =>
      api.patch(`/admin/roles/${key}`, { caps }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'roles'] })
      setDraft(null)
    },
  })

  const roles = rolesQuery.data?.items ?? []
  const counts = rolesQuery.data?.counts ?? {}
  const caps = metaQuery.data?.capabilities ?? []
  const role = roles.find((r) => r.key === selected)
  const locked = !!role && LOCKED_ROLES.has(role.key)
  const current = draft ?? role?.caps ?? []

  const groups = useMemo(() => {
    const out = new Map<string, Capability[]>()
    for (const c of caps) {
      if (!out.has(c.group)) out.set(c.group, [])
      out.get(c.group)!.push(c)
    }
    return [...out.entries()]
  }, [caps])

  function toggle(key: string) {
    setDraft((cur) => {
      const base = cur ?? role?.caps ?? []
      return base.includes(key) ? base.filter((c) => c !== key) : [...base, key]
    })
  }

  if (rolesQuery.isLoading || metaQuery.isLoading) {
    return <div className="stack"><Skeleton h={40} /><Skeleton h={200} /></div>
  }

  return (
    <div className="stack" style={{ gap: 'var(--sp-5)' }}>
      <Card>
        <CardHeader title="Choose a role" sub="Changes apply to everyone holding it" />
        <CardBody>
          <Select
            label="Role" value={selected}
            onChange={(e) => { setSelected(e.target.value); setDraft(null) }}
          >
            <option value="">Select a role…</option>
            {roles.map((r) => (
              <option key={r.key} value={r.key}>
                {r.title} — {counts[r.key] ?? 0} {counts[r.key] === 1 ? 'person' : 'people'}
                {r.active === false ? ' (switched off)' : ''}
              </option>
            ))}
          </Select>
        </CardBody>
      </Card>

      {role && (
        <Card>
          <CardHeader
            title={`What ${role.title} can do`}
            sub={role.description || `${titleCase(role.tier)} · ${titleCase(role.scope)} scope`}
          />
          <CardBody>
            <div className="stack" style={{ gap: 'var(--sp-4)' }}>
              {locked && (
                <Alert tone="info">
                  <strong>{role.title}</strong> is a core role defined in code, so its
                  permissions can’t be edited here. To give someone with this role an
                  extra ability, use <strong>By person</strong> instead.
                </Alert>
              )}
              {save.isError && (
                <Alert tone="danger">
                  {save.error instanceof ApiError ? save.error.message : 'Could not save those permissions.'}
                </Alert>
              )}

              {groups.map(([group, items]) => (
                <div key={group}>
                  <div className="nav__section" style={{ padding: '0 0 6px', color: 'var(--text-subtle)' }}>
                    {group}
                  </div>
                  <div className="stack" style={{ gap: 'var(--sp-2)' }}>
                    {items.map((c) => (
                      <label key={c.key} className="lrow" style={{ cursor: locked ? 'default' : 'pointer' }}>
                        <input
                          type="checkbox" disabled={locked}
                          checked={current.includes(c.key)}
                          onChange={() => toggle(c.key)}
                          style={{ marginRight: 'var(--sp-3)' }}
                        />
                        <div className="lrow__main">
                          <div className="lrow__title">{c.label}</div>
                          <div className="lrow__sub">{c.key}</div>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              ))}

              {!locked && (
                <div className="row">
                  <Button
                    loading={save.isPending}
                    disabled={draft === null}
                    onClick={() => save.mutate({ key: role.key, caps: current })}
                  >
                    Save permissions
                  </Button>
                  {draft !== null && (
                    <Button variant="secondary" onClick={() => setDraft(null)}>Discard changes</Button>
                  )}
                </div>
              )}
            </div>
          </CardBody>
        </Card>
      )}
    </div>
  )
}

/* ────────────────────────── By person ────────────────────────── */

function ByPerson() {
  const qc = useQueryClient()
  const [uid, setUid] = useState('')
  const [search, setSearch] = useState('')
  const [draftCaps, setDraftCaps] = useState<string[] | null>(null)
  const [draftRes, setDraftRes] = useState<Record<string, string[]> | null>(null)
  const [draftPrivate, setDraftPrivate] = useState<boolean | null>(null)

  const usersQuery = useQuery({
    queryKey: ['admin', 'users', 'permissions'],
    queryFn: () => api.get<{ ok: boolean; items: UserProfile[] }>('/admin/users?status=active&limit=1000'),
  })
  const metaQuery = useQuery({
    queryKey: ['admin', 'roles', 'meta'],
    queryFn: () => api.get<{ ok: boolean; capabilities: Capability[] }>('/admin/roles/meta'),
  })
  const detail = useQuery({
    queryKey: ['admin', 'user', uid],
    queryFn: () => api.get<{ ok: boolean; item: UserProfile }>(`/admin/users/${uid}`),
    enabled: !!uid,
  })
  const trucks = useQuery({
    queryKey: ['admin', 'trucks'],
    queryFn: () => api.get<{ ok: boolean; items: TruckOption[] }>('/admin/trucks'),
    enabled: !!uid,
  })
  const equipment = useQuery({
    queryKey: ['admin', 'hired-equipment'],
    queryFn: () => api.get<{ ok: boolean; items: EquipmentOption[] }>('/admin/hired-equipment'),
    enabled: !!uid,
  })
  const loads = useQuery({
    queryKey: ['admin', 'loads'],
    queryFn: () => api.get<{ ok: boolean; items: LoadOption[] }>('/admin/loads'),
    enabled: !!uid,
  })
  const sites = useQuery({
    queryKey: ['admin', 'sites'],
    queryFn: () => api.get<ListResponse<Site>>('/sites'),
    enabled: !!uid,
  })

  const save = useMutation({
    mutationFn: (body: Record<string, unknown>) => api.patch(`/admin/users/${uid}`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'user', uid] })
      void qc.invalidateQueries({ queryKey: ['admin', 'users', 'permissions'] })
      setDraftCaps(null)
      setDraftRes(null)
      setDraftPrivate(null)
    },
  })

  const users = usersQuery.data?.items ?? []
  const caps = metaQuery.data?.capabilities ?? []
  const person = detail.data?.item
  const filtered = users.filter((u) => {
    const q = search.trim().toLowerCase()
    if (!q) return true
    return `${u.full_name ?? ''} ${u.email ?? ''} ${u.role ?? ''}`.toLowerCase().includes(q)
  })

  const extra = draftCaps ?? ((person as { extra_caps?: string[] })?.extra_caps ?? [])
  const resource = (field: string): string[] =>
    draftRes?.[field] ?? ((person as unknown as Record<string, string[]>)?.[field] ?? [])

  function toggleCap(key: string) {
    setDraftCaps((cur) => {
      const base = cur ?? ((person as { extra_caps?: string[] })?.extra_caps ?? [])
      return base.includes(key) ? base.filter((c) => c !== key) : [...base, key]
    })
  }
  function toggleResource(field: string, id: string) {
    setDraftRes((cur) => {
      const base = { ...(cur ?? {}) }
      const list = base[field] ?? ((person as unknown as Record<string, string[]>)?.[field] ?? [])
      base[field] = list.includes(id) ? list.filter((x) => x !== id) : [...list, id]
      return base
    })
  }

  const privateAccess = draftPrivate
    ?? Boolean((person as { private_access?: boolean })?.private_access)

  const dirty = draftCaps !== null || draftRes !== null || draftPrivate !== null

  return (
    <div className="stack" style={{ gap: 'var(--sp-5)' }}>
      <Card>
        <CardHeader title="Choose a person" sub="Grants sit on top of whatever their role already allows" />
        <CardBody>
          <div className="stack" style={{ gap: 'var(--sp-4)' }}>
            <Input
              label="Search" placeholder="Name, email or role"
              value={search} onChange={(e) => setSearch(e.target.value)}
            />
            <Select
              label="Person" value={uid}
              onChange={(e) => { setUid(e.target.value); setDraftCaps(null); setDraftRes(null) }}
            >
              <option value="">Select a person…</option>
              {filtered.map((u) => (
                <option key={u.uid} value={u.uid}>
                  {u.full_name || u.email} — {titleCase(u.role ?? '')}
                </option>
              ))}
            </Select>
          </div>
        </CardBody>
      </Card>

      {uid && detail.isLoading && <Skeleton h={220} />}

      {person && (
        <>
          {save.isError && (
            <Alert tone="danger">
              {save.error instanceof ApiError ? save.error.message : 'Could not save those grants.'}
            </Alert>
          )}

          <Card>
            <CardHeader
              title="Extra abilities"
              sub={`On top of ${titleCase(person.role ?? '')}. Ticking nothing leaves their role unchanged.`}
            />
            <CardBody>
              <div className="stack" style={{ gap: 'var(--sp-2)' }}>
                {caps.map((c) => (
                  <label key={c.key} className="lrow" style={{ cursor: 'pointer' }}>
                    <input
                      type="checkbox" checked={extra.includes(c.key)}
                      onChange={() => toggleCap(c.key)}
                      style={{ marginRight: 'var(--sp-3)' }}
                    />
                    <div className="lrow__main">
                      <div className="lrow__title">{c.label}</div>
                      <div className="lrow__sub">{c.group} · {c.key}</div>
                    </div>
                  </label>
                ))}
              </div>
            </CardBody>
          </Card>

          <Card>
            <CardHeader
              title="Private works"
              sub="Granted to this person only — never to their role"
            />
            <CardBody>
              <label className="lrow" style={{ cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={privateAccess}
                  onChange={() => setDraftPrivate(!privateAccess)}
                  style={{ marginRight: 'var(--sp-3)' }}
                />
                <div className="lrow__main">
                  <div className="lrow__title">Can see private works</div>
                  <div className="lrow__sub">
                    Records belonging to people marked private are hidden from everyone
                    except super admins. Tick this to let this person see them too — for
                    a government officer investigating a specific matter, say. Leave it
                    off unless there is a reason.
                  </div>
                </div>
              </label>
            </CardBody>


          </Card>


          <ResourceGrant
            title="Trucks they may see" field="truck_access"
            loading={trucks.isLoading}
            options={(trucks.data?.items ?? []).map((t) => ({
              id: t.id ?? '', label: t.plate || t.id || '', sub: t.model || '',
            }))}
            selected={resource('truck_access')} onToggle={toggleResource}
          />
          <ResourceGrant
            title="Equipment they may see" field="equipment_access"
            loading={equipment.isLoading}
            options={(equipment.data?.items ?? []).map((e) => ({
              id: e.id ?? '', label: e.name || e.equipment_type || e.id || '', sub: e.equipment_type || '',
            }))}
            selected={resource('equipment_access')} onToggle={toggleResource}
          />
          <ResourceGrant
            title="Loads they may see" field="load_access"
            loading={loads.isLoading}
            options={(loads.data?.items ?? []).map((l) => ({
              id: l.id ?? '', label: l.ticket_no || l.id || '',
              sub: [titleCase(l.mineral_type ?? ''), l.tonnage ? `${l.tonnage} t` : ''].filter(Boolean).join(' · '),
            }))}
            selected={resource('load_access')} onToggle={toggleResource}
          />
          <ResourceGrant
            title="Sites they may see" field="site_access"
            loading={sites.isLoading}
            options={(sites.data?.items ?? []).map((s) => ({
              id: s.id ?? '', label: s.name || s.id || '',
              sub: [titleCase(s.district_id ?? ''), titleCase(s.region_id ?? '')].filter(Boolean).join(', '),
            }))}
            selected={resource('site_access')} onToggle={toggleResource}
          />

          <div className="row">
            <Button
              loading={save.isPending} disabled={!dirty}
              onClick={() => save.mutate({
                private_access: privateAccess,
                extra_caps: extra,
                truck_access: resource('truck_access'),
                equipment_access: resource('equipment_access'),
                load_access: resource('load_access'),
                site_access: resource('site_access'),
              })}
            >
              Save grants
            </Button>
            {dirty && (
              <Button variant="secondary" onClick={() => { setDraftCaps(null); setDraftRes(null); setDraftPrivate(null) }}>
                Discard changes
              </Button>
            )}
          </div>
        </>
      )}
    </div>
  )
}

function ResourceGrant({ title, field, options, selected, onToggle, loading }: {
  title: string
  field: string
  options: { id: string; label: string; sub?: string }[]
  selected: string[]
  onToggle: (field: string, id: string) => void
  loading?: boolean
}) {
  const [q, setQ] = useState('')
  const shown = options.filter((o) =>
    !q.trim() || `${o.label} ${o.sub ?? ''}`.toLowerCase().includes(q.trim().toLowerCase()))

  return (
    <Card>
      <CardHeader
        title={title}
        sub={selected.length
          ? `${selected.length} granted — they see only these`
          : 'Nothing granted — not restricted to a specific list'}
      />
      <CardBody>
        {loading ? (
          <Skeleton h={80} />
        ) : options.length === 0 ? (
          <EmptyState icon={<ShieldIcon size={22} />} title="Nothing to grant yet" />
        ) : (
          <div className="stack" style={{ gap: 'var(--sp-3)' }}>
            <Input placeholder="Search…" value={q} onChange={(e) => setQ(e.target.value)} />
            <div className="stack" style={{ gap: 'var(--sp-2)', maxHeight: 300, overflowY: 'auto' }}>
              {shown.map((o) => (
                <label key={o.id} className="lrow" style={{ cursor: 'pointer' }}>
                  <input
                    type="checkbox" checked={selected.includes(o.id)}
                    onChange={() => onToggle(field, o.id)}
                    style={{ marginRight: 'var(--sp-3)' }}
                  />
                  <div className="lrow__main">
                    <div className="lrow__title">{o.label}</div>
                    {o.sub && <div className="lrow__sub">{o.sub}</div>}
                  </div>
                </label>
              ))}
            </div>
          </div>
        )}
      </CardBody>
    </Card>
  )
}
